import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { RootState } from "../../../../../Controllers/Store";
import { useSelector } from "react-redux";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";
import { Nullable } from "primereact/ts-helpers";
import { Toast } from "primereact/toast";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import DatePicker from "../../../../../Components/DatePicker";
import ErrorPage from "../../../../../Components/ErrorPage";
import Textarea from "../../../../../Components/TextArea";
import AlertModal from "../../../../../Components/AlertModal";
import GoBackButton from "../../../../../Components/GoBackButton";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import { goToTop } from "../../../../../Components/goToTop";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { checkIsArchitect, checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { checkModelSelected, checkText } from "../../../../../Utils/Validator";

import { SiteVisitReport as SiteVisitReportType, Weather } from "../../../../../Controllers/Types/SiteVisitReport/SiteVisitReportType";
import { createSiteVisitReportAPI, updateSiteVisitReportAPI, deleteSiteVisitReportAPI } from "../../../../../Api/SiteVisitReport/SiteVisitReportApiServices";
import { fetchSiteVisitReports, fetchWeather } from "../../../../../Controllers/Thunks/SiteVisitReportThunks";

const SiteVisitReport: React.FC = () => {
    const today = new Date();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(false);
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();

    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);

    // Site visit report state values
    const {
        siteVisitReportLoading, siteVisitReportError, siteVisitReports,
        weatherLoading, weatherError, weathers
    } = useAppSelector((state) => state?.siteVisitReport);

    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [siteVisitReportUpdateHasChanges, setSiteVisitReportUpdateHasChanges] = useState<boolean>(false);
    const [siteVisitReportStatusFilterState, setSiteVisitReportStatusFilterState] = useState<'All' | 'Accepted' | 'Incomplete' | 'Complete'>('All');

    const [siteVisitReportData, setSiteVisitReportData] = useState<SiteVisitReportType[]>([]);
    const [selectedSiteVisitReportData, setSelectedSiteVisitReportData] = useState<SiteVisitReportType | null>(null);
    const [lastSiteVisitReportData, setLastSiteVisitReportData] = useState<SiteVisitReportType | null>(null);

    const [showSiteVisitReportModal, setShowSiteVisitReportModal] = useState<boolean>(false);

    /* Site visit report form fields */
    const [siteInspectionFor, setSiteInspectionFor] = useState<string>('');
    const [siteVisitReportDate, setSiteVisitReportDate] = useState<Nullable<Date>>(today);
    const [selectedWeather, setSelectedWeather] = useState<Weather | null>(null);

    /* Errors */
    const [siteInspectionForError, setSiteInspectionForError] = useState<string>('');
    const [siteVisitReportDateError, setSiteVisitReportDateError] = useState<string>('');
    const [siteVisitReportWeatherError, setSiteVisitReportWeatherError] = useState<string>('');


    /* PERMISION HANDLER */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
        };

        checkRole();
    }, []);
    /*  */

    const hasPermissionAddSiteVisitReport = useCanEdit("Site Visit Report", "add_site_visit_report");
    const hasPermissionEditSiteVisitReport = useCanEdit("Site Visit Report", "edit_site_visit_report");
    const hasPermissionDeleteSiteVisitReport = useCanEdit("Site Visit Report", "delete_site_visit_report");
    /*  */

    // Fetch data initially from state
    useEffect(() => {
        siteVisitReportDataGet();

        dispatch(fetchWeather());
    }, [dispatch, location?.state]);

    const siteVisitReportDataGet = async () => {
        await dispatch(fetchSiteVisitReports(folderId));
        setInitialDataLoading(false);
    }

    useEffect(() => {
        // Set site visit report data to the list
        if (siteVisitReports && siteVisitReports?.length > 0) {
            let filteredData: SiteVisitReportType[] = [];

            if (siteVisitReportStatusFilterState === 'All') {
                filteredData = siteVisitReports;
            } else {
                filteredData = siteVisitReports?.filter((data) => data.status === siteVisitReportStatusFilterState);
            }
            setSiteVisitReportData(filteredData);
            setLastSiteVisitReportData(siteVisitReports[siteVisitReports.length - 1]);
        } else {
            setSiteVisitReportData([]);
            setLastSiteVisitReportData(null);
        }
    }, [siteVisitReportStatusFilterState, siteVisitReports]);


    /* *********************************** For Builing inspector *********************************** */
    // Close site visit report modal
    const handleCloseSiteVisitReportModal = () => {
        setShowSiteVisitReportModal(false);
        handleResetSiteVisitReportFields();
        handleResetSiteVisitReportErrors();
    }

    // Create site visit report
    const handleAddSiteVisitReport = () => {
        setDataState('Add');
        setShowSiteVisitReportModal(true);
    }

    // Clear error messages
    useEffect(() => {
        checkText(siteInspectionFor, false, setSiteInspectionForError);
    }, [siteInspectionFor]);

    useEffect(() => {
        checkText(formatDate(siteVisitReportDate!), false, setSiteVisitReportDateError);
    }, [siteVisitReportDate]);

    useEffect(() => {
        checkModelSelected(selectedWeather !== null, false, setSiteVisitReportWeatherError);
    }, [selectedWeather]);

    // Check credentials for create site visit report
    function checkCredentialsSiteVisitReport(): boolean {
        const isValidReportInspectionFor = checkText(siteInspectionFor, true, setSiteInspectionForError);
        const isValidReportDate = checkText(formatDate(siteVisitReportDate!), true, setSiteVisitReportDateError);
        const isValidReportWeather = checkModelSelected(selectedWeather !== null, true, setSiteVisitReportWeatherError)

        return (
            isValidReportInspectionFor &&
            isValidReportDate &&
            isValidReportWeather
        );
    }

    // Get api error messages
    function getApiErrorSiteVisitReport(keyName: string, error: string): void {
        if (keyName === "inspection_for") {
            setSiteInspectionForError(error);
        }

        if (keyName === "weather_id") {
            setSiteVisitReportWeatherError(error);
        }

        if (keyName === "date") {
            setSiteVisitReportDateError(error);
        }
    }

    // Create site visit report API
    const handleCreateSiteVisitReport = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsSiteVisitReport() || !folderId) return;

        setLoading(true);

        try {
            const folder_id: number = folderId;
            const weather_id: number = selectedWeather?.id ?? 0;
            const date: string = formatDate(siteVisitReportDate ?? null);
            const inspection_for: string = siteInspectionFor;

            const data = await createSiteVisitReportAPI(
                folder_id,
                weather_id,
                date,
                inspection_for,
            );

            if (data?.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseSiteVisitReportModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Site visit report created successfully.",
                    life: 4000,
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    getApiErrorSiteVisitReport(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the site visit report.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error creating the site visit report:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the site visit report.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    // Edit site visit report
    const handleEditSiteVisitReport = (data: SiteVisitReportType) => {
        if (data && data !== null) {
            setDataState('Edit');
            setSelectedSiteVisitReportData(data);
            setShowSiteVisitReportModal(true);

            setSiteInspectionFor(data?.inspection_for ?? '');
            setSiteVisitReportDate(data?.date ? parseStringToDate(data?.date) : null);
            setSelectedWeather(data?.weather ?? null);
        }
    }

    useEffect(() => {
        if (!selectedSiteVisitReportData) return;

        if (siteInspectionFor !== null && siteInspectionFor !== selectedSiteVisitReportData?.inspection_for) {
            setSiteVisitReportUpdateHasChanges(true);
        } else if (formatDate(siteVisitReportDate!) !== selectedSiteVisitReportData?.date) {
            setSiteVisitReportUpdateHasChanges(true);
        } else if (selectedWeather?.id !== selectedSiteVisitReportData?.weather_id) {
            setSiteVisitReportUpdateHasChanges(true);
        } else {
            setSiteVisitReportUpdateHasChanges(false);
        }
    }, [selectedSiteVisitReportData, siteInspectionFor, siteVisitReportDate, selectedWeather]);

    // Update site visit report API
    const handleUpdateSiteVisitReport = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsSiteVisitReport() || !selectedSiteVisitReportData) return;

        setLoading(true);

        try {
            const site_visit_id: number = selectedSiteVisitReportData?.id!;
            const weather_id: number = selectedWeather?.id ?? 0;
            const date: string = formatDate(siteVisitReportDate!);
            const inspection_for: string = siteInspectionFor;

            const data = await updateSiteVisitReportAPI(
                site_visit_id,
                weather_id,
                date,
                inspection_for,
            );

            if (data?.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseSiteVisitReportModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Site visit report updated successfully.",
                    life: 4000,
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    getApiErrorSiteVisitReport(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating the site visit report.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating the site visit report:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the site visit report.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    // Delete site visit report
    const handleDeleteSiteVisitReport = () => {
        if (selectedSiteVisitReportData) {
            confirmDialog({
                message: `Are you sure you want to delete this site visit report ${selectedSiteVisitReportData?.site_visit_no}?`,
                header: 'Confirmation',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-danger',
                accept: () => deleteSiteVisitReport(selectedSiteVisitReportData?.id!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    // Delete site visit report API
    const deleteSiteVisitReport = async (siteVisitReportId: number) => {
        if (!siteVisitReportId) return;

        setDeleteLoading(true);

        try {
            const result = await deleteSiteVisitReportAPI(siteVisitReportId);

            if (result?.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseSiteVisitReportModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Site visit report deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the site visit report.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the site visit report:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the site visit report.",
                life: 3000,
            });
        } finally {
            setDeleteLoading(false);
        }
    }

    // Reset site visit report fields
    const handleResetSiteVisitReportFields = () => {
        setSiteInspectionFor('');
        setSiteVisitReportDate(today);
        setSelectedWeather(null);

        setSelectedSiteVisitReportData(null);
    }

    const handleResetSiteVisitReportErrors = () => {
        setSiteInspectionForError('');
        setSiteVisitReportDateError('');
        setSiteVisitReportWeatherError('');
    }

    // Site visit report modal header
    const siteVisitReportModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState === 'Add' ? (
                    <React.Fragment>
                        Create site visit report&nbsp;
                        {lastSiteVisitReportData !== null ? (lastSiteVisitReportData?.site_visit_no + 1) : 1}&nbsp;
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        Edit site visit report&nbsp;
                        {lastSiteVisitReportData !== null ? (selectedSiteVisitReportData?.site_visit_no) : ''}&nbsp;
                    </React.Fragment>
                )}
            </h5>

            {dataState === "Edit" && hasPermissionDeleteSiteVisitReport ? (
                <React.Fragment>
                    {deleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <button
                            type="button"
                            className="close_modal_btn p-ripple"
                            onClick={handleDeleteSiteVisitReport}
                        >
                            <i className="bi bi-trash3"></i>
                            <Ripple />
                        </button>
                    )}
                </React.Fragment>
            ) : (
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={handleCloseSiteVisitReportModal}
                >
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    );

    // Site visit report modal footer
    const siteVisitReportModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseSiteVisitReportModal}
            />

            <Button
                label={`${loading ? "Processing" : dataState === "Add" ? "Save" : "Update"}`}
                onClick={dataState === "Add" ? handleCreateSiteVisitReport : handleUpdateSiteVisitReport}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !siteVisitReportUpdateHasChanges}
            />
        </div>
    );
    /* ************************************************************************************* */


    /* ********************************* Site visit report common ********************************* */
    const handleFilterSiteVisitReport = (status: 'All' | 'Accepted' | 'Incomplete' | 'Complete') => {
        setSiteVisitReportStatusFilterState(status);
    }

    const handleViewSiteVisitReport = (data: SiteVisitReportType) => {
        if (!data) return;

        const isSignedReport: boolean = data?.sign_report === 1;

        if (isArchitect) {
            if (isSignedReport === true) {
                goToTop();
                navigate(`detail`, { state: { projectData: projectData, siteVisitReportData: data } });
            } else {
                setShowAlertModal(true);
                setAlertMessage(`That report has not yet been signed by the building inspector.`)
            }
        } else if (isInspector) {
            navigate(`detail`, { state: { projectData: projectData, siteVisitReportData: data } });
        }
    }

    const statusBody = (rowData: SiteVisitReportType) => {
        const status = rowData.status;
        let color = "";
        let bgColor = "";

        if (status === 'Accepted') {
            color = "#008cc3";
            bgColor = "#daf2fb";
        } else if (status === 'Complete') {
            color = "#06b500";
            bgColor = "#d5f8df";
        } else if (status === 'Incomplete') {
            color = "#f60000";
            bgColor = "#fee2e2";
        }

        return (
            <div className="flex_center">
                <span className="priority_label lg" style={{ color: color, backgroundColor: bgColor }}>
                    {status === 'Complete' ? 'Completed' : status}
                </span>
            </div>
        );
    };

    const tableActionBody = (rowData: SiteVisitReportType) => {
        const isSignedReport: boolean = rowData?.sign_report === 1;

        return (
            <React.Fragment>
                <div className={`table_action_btn_area justify-content-end mt-1 mb-1`}>
                    <button
                        className="table_action_btn fixed_width p-ripple"
                        onClick={() => handleViewSiteVisitReport(rowData)}
                    >
                        View
                        <Ripple />
                    </button>

                    {isInspector && hasPermissionEditSiteVisitReport && (
                        <button
                            className="table_action_btn fixed_width p-ripple"
                            onClick={() => handleEditSiteVisitReport(rowData)}
                            disabled={isSignedReport}
                        >
                            Edit
                            <Ripple />
                        </button>
                    )}
                </div>
            </React.Fragment>
        )
    }
    /* ************************************************************************************* */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {siteVisitReportLoading && initialDataLoading ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : siteVisitReportError ? (
                    <ErrorPage errorMessage={siteVisitReportError} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${siteVisitReportStatusFilterState === "All" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteVisitReport('All')}
                                        disabled={siteVisitReports?.length === 0}
                                    >
                                        All : {String(siteVisitReports?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${siteVisitReportStatusFilterState === "Accepted" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteVisitReport("Accepted")}
                                        disabled={siteVisitReports?.length === 0}
                                    >
                                        Accepted : {String(siteVisitReports?.filter(data => data?.status === 'Accepted')?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${siteVisitReportStatusFilterState === "Incomplete" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteVisitReport("Incomplete")}
                                        disabled={siteVisitReports?.length === 0}
                                    >
                                        Incomplete : {String(siteVisitReports?.filter(data => data?.status === 'Incomplete')?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${siteVisitReportStatusFilterState === "Complete" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteVisitReport("Complete")}
                                        disabled={siteVisitReports?.length === 0}
                                    >
                                        Completed : {String(siteVisitReports?.filter(data => data?.status === 'Complete')?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>
                            </div>

                            {isInspector && hasPermissionAddSiteVisitReport && (
                                <div className="page_action_area">
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddSiteVisitReport}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add site visit report</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section no_header">
                            {siteVisitReportData && siteVisitReportData?.length > 0 ? (
                                <DataTable
                                    value={siteVisitReportData}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    size="small"
                                    tableStyle={{ minWidth: "50rem" }}
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    loading={siteVisitReportLoading}
                                >
                                    <Column
                                        header="Site visit report no."
                                        field="site_visit_no"
                                        headerClassName="text_purple"
                                        body={(rowData: SiteVisitReportType) => (
                                            <span className="text_bold text_no_wrap">
                                                {rowData.site_visit_no ? ('Site visit report ' + rowData.site_visit_no) : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Inspection for"
                                        field="inspection_for"
                                        headerClassName="text_purple"
                                        body={(rowData: SiteVisitReportType) => (
                                            <span className="text_bold text_no_wrap">
                                                {rowData.inspection_for ? rowData.inspection_for : "---"}
                                            </span>
                                        )}
                                        style={{ width: "30%" }}
                                    ></Column>

                                    {siteVisitReportStatusFilterState === 'All' && (
                                        <Column
                                            header="Status"
                                            field="status"
                                            alignHeader={"center"}
                                            headerClassName="text_purple"
                                            body={statusBody}
                                            style={{ width: "12%" }}
                                        ></Column>
                                    )}

                                    <Column
                                        header="Date"
                                        field="date"
                                        headerClassName="text_purple"
                                        body={(rowData: SiteVisitReportType) => (
                                            <span className="text_no_wrap">
                                                {rowData.date ? rowData.date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "13%" }}
                                    ></Column>

                                    <Column
                                        header="Weather"
                                        field="weather.weather_name"
                                        headerClassName="text_purple"
                                        body={(rowData: SiteVisitReportType) => (
                                            <span className="text_no_wrap">
                                                {rowData.weather?.weather_name ? rowData.weather?.weather_name : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        body={tableActionBody}
                                        className="table_btn_area"
                                        style={{ width: "15%" }}
                                        frozen={window.innerWidth > 575}
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated message={
                                    `No ${siteVisitReportStatusFilterState === 'All' ? '' : siteVisitReportStatusFilterState} site visit reports found. 
                                    ${hasPermissionAddSiteVisitReport && isInspector && siteVisitReportStatusFilterState === 'All' ? 'Click the Add button to create a new site visit report.' : ''}`
                                } />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Site vist report modal */}
            <Dialog
                visible={showSiteVisitReportModal}
                header={siteVisitReportModalHeader}
                footer={siteVisitReportModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseSiteVisitReportModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pt-sm-2 pb-sm-0 w-100">
                        <div className="row">
                            {/* Inspection for */}
                            <div className="col-12">
                                <Textarea
                                    key={`siteInspectionFor`}
                                    id="siteInspectionFor"
                                    label="Inspection for"
                                    labelHtmlFor="siteInspectionFor"
                                    required={true}
                                    value={siteInspectionFor}
                                    placeholder="Site inspection for"
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSiteInspectionFor(e.target.value)}
                                    error={siteInspectionForError}
                                />
                            </div>

                            {/* Date */}
                            <div className="col-12 col-sm-6">
                                <DatePicker
                                    key={`siteVisitReportDate`}
                                    id="siteVisitReportDate"
                                    label="Date"
                                    labelHtmlFor="siteVisitReportDate"
                                    required={true}
                                    value={siteVisitReportDate}
                                    minDate={today}
                                    placeholder="dd/mm/yyyy"
                                    onChange={setSiteVisitReportDate}
                                    error={siteVisitReportDateError}
                                    showClear
                                    onClear={() => setSiteVisitReportDate(null)}
                                />
                            </div>

                            {/* Weather */}
                            <div className="col-12 col-sm-6">
                                <div className="page_form_group mb-3">
                                    <label htmlFor="siteVisitReportWeather" className="custom_form_label is_required">Weather</label>
                                    <Dropdown
                                        key={`siteVisitReportWeather`}
                                        id="siteVisitReportWeather"
                                        value={selectedWeather}
                                        onChange={(e: DropdownChangeEvent) => setSelectedWeather(e.value)}
                                        optionLabel="weather_name"
                                        options={weathers}
                                        panelClassName="status_option_panel"
                                        placeholder="Select weather"
                                        filterPlaceholder="Search weather"
                                        className="form_dropdown w-100"
                                        loading={weatherLoading}
                                        filter
                                    />

                                    {weatherError &&
                                        <small className="form_error_msg">{weatherError}</small>
                                    }

                                    {siteVisitReportWeatherError &&
                                        <small className="form_error_msg">{siteVisitReportWeatherError}</small>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Alert modal */}
            <AlertModal
                isOpen={showAlertModal}
                onClose={() => setShowAlertModal(false)}
                message={alertMessage}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default SiteVisitReport;